/**
 * `/admin` — the surface without which several §8 promises are unkeepable.
 *
 * The refusal text is Admin-configured and never hardcoded, `export_sensitive`
 * is set by a human rather than derived from a segment, and the compliance
 * record is something a person has to be able to read. All three need a screen.
 *
 * Every write here is Admin-only and audited, and **the UI does not offer a
 * control the API will refuse** — where Phase 3 provides no write route (the
 * stage table, reason codes), the screen is explicitly read-only and says why.
 */

import { NavLink, Navigate, Route, Routes } from 'react-router-dom';
import { useMe } from '@/auth/useMe';
import { AdminCompliance } from './AdminCompliance';
import { AdminImport } from './AdminImport';
import { AdminUsers } from './AdminUsers';
import { AdminReference } from './AdminReference';
import { AdminSettings } from './AdminSettings';
import { cn } from '@/lib/utils';

const SECTIONS = [
  { to: '/admin/compliance', label: 'Compliance' },
  { to: '/admin/users', label: 'Users' },
  { to: '/admin/reference', label: 'Reference data' },
  { to: '/admin/import', label: 'Import' },
  { to: '/admin/settings', label: 'Settings' },
];

export function AdminLayout(): React.ReactElement {
  const { data: me } = useMe();

  // Convenience only — §2 rule 1 puts enforcement on the server, and every
  // endpoint below refuses a non-Admin regardless of what this renders.
  if (me !== undefined && me.tier !== 'Admin') {
    return (
      <div className="max-w-lg rounded-lg border bg-card p-6">
        <h1 className="text-base font-semibold">Administration</h1>
        <p className="mt-2 text-sm text-muted-foreground">
          This area is for administrators. Your access level is {me.tier}.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <header>
        <h1 className="text-lg font-semibold">Administration</h1>
        <p className="text-xs text-muted-foreground">
          Every change here is audited. Roles are assigned in Entra and nowhere else.
        </p>
      </header>

      <nav aria-label="Admin sections" className="flex gap-1 border-b">
        {SECTIONS.map((section) => (
          <NavLink
            key={section.to}
            to={section.to}
            className={({ isActive }) =>
              cn(
                '-mb-px border-b-2 px-3 py-2 text-sm',
                isActive
                  ? 'border-primary font-medium'
                  : 'border-transparent text-muted-foreground hover:text-foreground',
              )
            }
          >
            {section.label}
          </NavLink>
        ))}
      </nav>

      <Routes>
        <Route index element={<Navigate to="/admin/compliance" replace />} />
        <Route path="compliance" element={<AdminCompliance />} />
        <Route path="users" element={<AdminUsers />} />
        <Route path="reference" element={<AdminReference />} />
        <Route path="import" element={<AdminImport />} />
        <Route path="settings" element={<AdminSettings />} />
      </Routes>
    </div>
  );
}
